import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  Chip,
  Badge,
  Stack,
  InputAdornment,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ReactCountryFlag from 'react-country-flag';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { useToaster } from 'src/utils/toaster/toasterContext';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { getLiveChatAgentContext } from 'src/utils/liveChatNotifications';
import {
  addLiveChatSocketErrorListener,
  addLiveChatSocketListener,
  addLiveChatSocketOpenListener,
  connectLiveChatSocket,
  isLiveChatSocketOpen,
  sendLiveChatSocketMessage,
} from 'src/utils/liveChatSocket';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const GATEWAY_HTTP_URL = (process.env.REACT_APP_API_URL || `${window.location.origin}/api`).replace(/\/+$/, '');
const CONVERSATION_PAGE_SIZE = 20;

const getQueryNumber = (key) => {
  if (typeof window === 'undefined') return 0;
  return Number(new URLSearchParams(window.location.search).get(key) || 0);
};

const getCountryCode = (country) => {
  const code = String(country || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return code;
};

const getConversationCountryCode = (conv) => {
  const candidates = [conv?.country_flag, conv?.visitor_country, conv?.country];
  for (const value of candidates) {
    const code = getCountryCode(value);
    if (code) return code;
  }
  return '';
};

const getValidDate = (value) => {
  if (!value) return null;
  const date = CommonHelper.getBrandTimeZoneTime(value);
  return date?.isValid?.() ? date : null;
};

const VisitorFlagAvatar = ({ conversation, fallback }) => {
  const countryCode = getConversationCountryCode(conversation);
  return (
    <Avatar sx={{ bgcolor: countryCode ? '#fff' : '#e3f2fd', color: 'text.secondary' }}>
      {countryCode ? (
        <ReactCountryFlag
          countryCode={countryCode}
          svg
          style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
          title={countryCode}
        />
      ) : (
        fallback
      )}
    </Avatar>
  );
};

const AgentInbox = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [conversationPage, setConversationPage] = useState(1);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [conversationsLoader, setConversationsLoader] = useState(false);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typingState, setTypingState] = useState({});
  const [onlineAgents, setOnlineAgents] = useState({});
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [closeLoader, setCloseLoader] = useState(false);
  const [statusConfirmAction, setStatusConfirmAction] = useState(null);
  const [createTicketLoader, setCreateTicketLoader] = useState(false);
  const [createTicketConfirmOpen, setCreateTicketConfirmOpen] = useState(false);
  const [ticketProviderConfigured, setTicketProviderConfigured] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);

  const { showToast } = useToaster();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectedConvRef = useRef(null);
  const typingTimer = useRef(null);
  const incomingTypingTimers = useRef({});
  const threadRefreshTimer = useRef(null);
  const conversationsLoaderRef = useRef(false);
  const conversationPageRef = useRef(1);
  const hasMoreConversationsRef = useRef(true);
  const shouldAutoScrollMessagesRef = useRef(false);
  const targetConversationIdRef = useRef(getQueryNumber('conversation_id'));

  const currentUserInfo = CommonHelper.GetBrandInfoFromLocalStorage('user_details') || {};
  const currentUserId = Number(currentUserInfo.user_id || currentUserInfo.id || 0);
  const hasCurrentUserId = currentUserId > 0;

  const isMessagesNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 120;
  };

  // Scroll chat container only (prevents page jump under fixed header)
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (!shouldAutoScrollMessagesRef.current) return;
    requestAnimationFrame(() => {
      scrollToBottom();
      shouldAutoScrollMessagesRef.current = false;
    });
  }, [messages]);

  const mergeConversations = (current, incoming, incomingFirst = false) => {
    const seen = new Set();
    const ordered = incomingFirst ? [...incoming, ...current] : [...current, ...incoming];
    return ordered.filter((conversation) => {
      if (seen.has(conversation.id)) return false;
      seen.add(conversation.id);
      return true;
    });
  };

  // Fetch conversations, including previous closed chats for history.
  const fetchConversations = ({
    nextStatus = statusFilter,
    page = 1,
    append = false,
    preserveExisting = false,
    silent = false,
  } = {}) => {
    if (append && (conversationsLoaderRef.current || !hasMoreConversationsRef.current)) return;
    conversationsLoaderRef.current = true;
    if (!silent) setConversationsLoader(true);

    const queryParams = new URLSearchParams({
      page: String(page),
      limit: String(CONVERSATION_PAGE_SIZE),
    });
    if (nextStatus && nextStatus !== 'all') {
      queryParams.set('status', nextStatus);
    }

    HttpServices.Get({
      url: `${GATEWAY_HTTP_URL}/live-chat-services/conversations?${queryParams.toString()}`,
      successCallback: (data) => {
        const nextConversations = Array.isArray(data) ? data : data?.conversations || [];
        const nextPage = Array.isArray(data) ? page : data?.page || page;
        const nextHasMore = Array.isArray(data) ? nextConversations.length >= CONVERSATION_PAGE_SIZE : Boolean(data?.has_more);

        setConversationPage(nextPage);
        conversationPageRef.current = nextPage;
        setHasMoreConversations(nextHasMore);
        hasMoreConversationsRef.current = nextHasMore;
        setConversations((prev) => {
          if (append) return mergeConversations(prev, nextConversations);
          if (preserveExisting) return mergeConversations(prev, nextConversations, true);
          return nextConversations;
        });
        const targetConversationId = Number(targetConversationIdRef.current || 0);
        if (targetConversationId && !selectedConvRef.current?.id) {
          const targetConversation = nextConversations.find(
            (conversation) => Number(conversation.id) === targetConversationId
          );
          if (targetConversation) {
            setSelectedConv(targetConversation);
            fetchMessages(targetConversation.id, { forceScrollToBottom: true });
            targetConversationIdRef.current = 0;
          }
        }
        conversationsLoaderRef.current = false;
        setConversationsLoader(false);
      },
      failureCallback: (msg) => {
        conversationsLoaderRef.current = false;
        setConversationsLoader(false);
        showToast(
          msg ||
            translate('agent_inbox_page.failed_to_load_conversations', {
              defaultValue: 'Failed to load conversations',
            }),
          'e'
        );
      },
    });
  };

  const fetchAgentPresence = () => {
    HttpServices.Get({
      url: `${GATEWAY_HTTP_URL}/live-chat-services/agent-presence`,
      successCallback: (data) => {
        const nextAgents = {};
        (data || []).forEach((agent) => {
          nextAgents[agent.user_id] = agent.status === 'online';
        });
        setOnlineAgents(nextAgents);
      },
      failureCallback: () => {},
    });
  };

  // Fetch Messages
  const fetchMessages = (convId, options = {}) => {
    shouldAutoScrollMessagesRef.current = Boolean(options.forceScrollToBottom) || isMessagesNearBottom();
    HttpServices.Get({
      url: `${GATEWAY_HTTP_URL}/live-chat-services/conversations/${convId}/messages`,
      successCallback: (data) => setMessages(data || []),
    });
  };

  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  useEffect(() => {
    HttpServices.Get({
      url: userModuleURL.ticketMailProvider,
      successCallback: (data) => {
        setTicketProviderConfigured(Boolean(data?.is_active && data?.is_configured));
      },
      failureCallback: () => {
        setTicketProviderConfigured(false);
      },
    });
  }, []);

  useEffect(() => {
    setConversationPage(1);
    conversationPageRef.current = 1;
    setHasMoreConversations(true);
    hasMoreConversationsRef.current = true;
    fetchConversations({ nextStatus: statusFilter, page: 1 });
  }, [statusFilter]);

  // WebSocket Connection
  useEffect(() => {
    const agentContext = getLiveChatAgentContext();
    if (!agentContext.userId || !agentContext.brandId) {
      showToast(
        translate('agent_inbox_page.socket_registration_failed', {
          defaultValue: 'Live chat socket registration failed',
        }),
        'e'
      );
      return undefined;
    }

    const refreshInbox = () => {
      fetchConversations({ page: 1, preserveExisting: true, silent: true });
      fetchAgentPresence();
    };

    const removeOpenListener = addLiveChatSocketOpenListener(refreshInbox);
    const removeErrorListener = addLiveChatSocketErrorListener(() => {
      showToast(
        translate('agent_inbox_page.connection_error', {
          defaultValue: 'Live chat connection error',
        }),
        'e'
      );
    });
    const removeMessageListener = addLiveChatSocketListener((data) => {
      const payload = data.payload || {};

      if (data.type === 'agent:registered') {
        return;
      }

      if (data.type === 'ws:error') {
        showToast(
          payload.message ||
            translate('agent_inbox_page.socket_registration_failed', {
              defaultValue: 'Live chat socket registration failed',
            }),
          'e'
        );
      }

      if (data.type === 'conversation:new') {
        const newConv = payload;
        setConversations((prev) => [newConv, ...prev.filter((c) => c.id !== newConv.id)]);
        showToast(
          translate('agent_inbox_page.new_chat_from', {
            defaultValue: 'New chat from {{name}}',
            name:
              newConv.visitor_name ||
              translate('agent_inbox_page.visitor', { defaultValue: 'Visitor' }),
          }),
          'info'
        );
      }

      if (data.type === 'conversation:update') {
        setConversations((prev) => prev.map((conv) => (conv.id === payload.id ? { ...conv, ...payload } : conv)));
        if (selectedConvRef.current && payload.id === selectedConvRef.current.id) {
          setSelectedConv((prev) => ({ ...prev, ...payload }));
        }
      }

      if (data.type === 'message:new') {
        const msg = payload;
        const selectedConvId = Number(selectedConvRef.current?.id || 0);
        const incomingConvId = Number(msg.conversation_id || 0);
        if (selectedConvId > 0 && incomingConvId === selectedConvId) {
          // Always fetch canonical thread from server to avoid local state drift.
          fetchMessages(selectedConvId);
          if (msg.sender_type !== 'agent') {
            if (incomingTypingTimers.current.visitor) {
              clearTimeout(incomingTypingTimers.current.visitor);
            }
            setTypingState((prev) => ({ ...prev, visitor: false }));
            sendLiveChatSocketMessage({
              type: 'message:read',
              payload: { conversation_id: msg.conversation_id, message_id: msg.id },
            });
          }
        }
        fetchConversations({ page: 1, preserveExisting: true, silent: true });
      }

      if (data.type === 'message:read') {
        // hook for read receipts in future
      }

      if (data.type === 'visitor:typing' || data.type === 'agent:typing') {
        if (selectedConvRef.current && payload.conversation_id === selectedConvRef.current.id) {
          // Ignore my own typing echo from socket
          if (hasCurrentUserId && payload.sender_type === 'agent' && Number(payload.sender_id) === currentUserId) {
            return;
          }
          if (incomingTypingTimers.current[payload.sender_type]) {
            clearTimeout(incomingTypingTimers.current[payload.sender_type]);
          }
          setTypingState((prev) => ({
            ...prev,
            [payload.sender_type]: payload.is_typing,
          }));
          if (payload.is_typing) {
            incomingTypingTimers.current[payload.sender_type] = setTimeout(() => {
              setTypingState((prev) => ({
                ...prev,
                [payload.sender_type]: false,
              }));
            }, 2500);
          }
        }
      }

      if (data.type === 'agent:online' || data.type === 'agent:offline') {
        setOnlineAgents((prev) => ({
          ...prev,
          [payload.user_id]: data.type === 'agent:online',
        }));
      }
    });

    connectLiveChatSocket();
    if (isLiveChatSocketOpen()) {
      refreshInbox();
    }

    return () => {
      if (typingTimer.current) clearTimeout(typingTimer.current);
      Object.values(incomingTypingTimers.current).forEach((timer) => clearTimeout(timer));
      if (threadRefreshTimer.current) clearInterval(threadRefreshTimer.current);
      removeOpenListener();
      removeErrorListener();
      removeMessageListener();
    };
  }, []);

  useEffect(() => {
    if (threadRefreshTimer.current) {
      clearInterval(threadRefreshTimer.current);
      threadRefreshTimer.current = null;
    }
    if (!selectedConv?.id) return;
    // Fallback live refresh for selected thread (covers missed WS events).
    threadRefreshTimer.current = setInterval(() => {
      fetchMessages(selectedConv.id);
      fetchConversations({ page: 1, preserveExisting: true, silent: true });
    }, 2000);
    return () => {
      if (threadRefreshTimer.current) {
        clearInterval(threadRefreshTimer.current);
        threadRefreshTimer.current = null;
      }
    };
  }, [selectedConv?.id]);

  const handleSelectConversation = (conv) => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    if (selectedConv?.id && isLiveChatSocketOpen()) {
      sendLiveChatSocketMessage({
        type: 'agent:typing',
        payload: { conversation_id: selectedConv.id, is_typing: false },
      });
    }
    Object.values(incomingTypingTimers.current).forEach((timer) => clearTimeout(timer));
    incomingTypingTimers.current = {};
    setTypingState({});
    setSelectedConv(conv);
    setMediaFiles([]);
    fetchMessages(conv.id, { forceScrollToBottom: true });
    if (isLiveChatSocketOpen()) {
      sendLiveChatSocketMessage({
        type: 'message:read',
        payload: { conversation_id: conv.id },
      });
    }
  };

  const handleStatusFilterChange = (_event, nextStatus) => {
    if (!nextStatus) return;
    setStatusFilter(nextStatus);
    setSelectedConv(null);
    setMessages([]);
    setMediaFiles([]);
    setSearchTerm('');
  };

  const handleConversationsScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight < scrollHeight - 120) return;
    fetchConversations({
      nextStatus: statusFilter,
      page: conversationPage + 1,
      append: true,
      silent: true,
    });
  };

  const closeConversation = () => {
    if (!selectedConv?.id || selectedConv.status === 'closed') return;
    setCloseLoader(true);
    HttpServices.Post({
      url: `${GATEWAY_HTTP_URL}/live-chat-services/conversations/${selectedConv.id}/close`,
      body: {},
      successCallback: (data, message) => {
        const nextConv = { ...selectedConv, ...(data || {}), status: 'closed' };
        setSelectedConv(nextConv);
        setConversations((prev) => prev.map((conv) => (conv.id === nextConv.id ? { ...conv, ...nextConv } : conv)));
        setCloseLoader(false);
        setStatusConfirmAction(null);
        showToast(
          message ||
            translate('agent_inbox_page.conversation_closed_successfully', {
              defaultValue: 'Conversation closed successfully',
            }),
          's'
        );
      },
      failureCallback: (message) => {
        setCloseLoader(false);
        setStatusConfirmAction(null);
        showToast(
          message ||
            translate('agent_inbox_page.failed_to_close_conversation', {
              defaultValue: 'Failed to close conversation',
            }),
          'e'
        );
      },
    });
  };

  const confirmConversationStatusChange = () => {
    if (statusConfirmAction === 'close') {
      closeConversation();
    }
  };

  const createTicketFromConversation = () => {
    if (!ticketProviderConfigured) {
      showToast(
        translate('agent_inbox_page.configure_smtp_before_ticket', {
          defaultValue: 'Configure SMTP ticket email before creating tickets',
        }),
        'e'
      );
      return;
    }
    if (!selectedConv?.id || selectedConv.status === 'closed') return;
    setCreateTicketLoader(true);
    HttpServices.Post({
      url: userModuleURL.createTicketFromConversation,
      body: {
        conversation_id: Number(selectedConv.id),
        subject: selectedConv.subject || selectedConv.last_question || 'Live chat follow-up',
        message: selectedConv.last_question || '',
      },
      successCallback: (data, message) => {
        const ticket = data?.ticket || {};
        const closedConv = { ...selectedConv, status: 'closed' };
        setSelectedConv(closedConv);
        setConversations((prev) =>
          prev.map((conv) => (Number(conv.id) === Number(closedConv.id) ? { ...conv, status: 'closed' } : conv))
        );
        setCreateTicketLoader(false);
        setCreateTicketConfirmOpen(false);
        showToast(
          message ||
            translate('agent_inbox_page.ticket_created_successfully', {
              defaultValue: 'Ticket created successfully',
            }),
          's'
        );
        navigate(ticket?.id ? `/tickets?ticket_id=${ticket.id}` : '/tickets');
      },
      failureCallback: (message) => {
        setCreateTicketLoader(false);
        setCreateTicketConfirmOpen(false);
        showToast(
          message ||
            translate('agent_inbox_page.failed_to_create_ticket', {
              defaultValue: 'Failed to create ticket',
            }),
          'e'
        );
      },
    });
  };

  const sendMessage = () => {
    if (
      (!newMessage.trim() && mediaFiles.length === 0) ||
      !selectedConv ||
      selectedConv.status === 'closed'
    )
      return;
    const mediaURL = mediaFiles
      .map((file) => file.media_url)
      .filter(Boolean)
      .join(',');

    const messageData = {
      type: 'message:send',
      payload: {
        conversation_id: selectedConv.id,
        message: newMessage.trim(),
        media_url: mediaURL,
        message_type: mediaURL ? 'media' : 'text',
        sender_type: 'agent',
        sender_id: currentUserId,
      },
    };

    if (!sendLiveChatSocketMessage(messageData)) {
      showToast(
        translate('agent_inbox_page.connection_error', {
          defaultValue: 'Live chat connection error',
        }),
        'e'
      );
      return;
    }
    // Fallback refresh so message appears even if socket broadcast is delayed/missed.
    setTimeout(() => {
      fetchMessages(selectedConv.id, { forceScrollToBottom: true });
      fetchConversations({ page: 1, preserveExisting: true, silent: true });
    }, 250);

    setNewMessage('');
    setMediaFiles([]);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    sendLiveChatSocketMessage({
      type: 'agent:typing',
      payload: { conversation_id: selectedConv.id, is_typing: false },
    });
  };

  const handleMediaUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const unsupportedFile = files.find((file) => !file.type.startsWith('image/') && !file.type.startsWith('video/'));
    if (unsupportedFile) {
      showToast(
        translate('agent_inbox_page.only_image_video_allowed', {
          defaultValue: 'Only image and video files are allowed',
        }),
        'e'
      );
      event.target.value = '';
      return;
    }
    if (files.some((file) => file.size > 25 * 1024 * 1024)) {
      showToast(
        translate('agent_inbox_page.file_size_limit', {
          defaultValue: 'Each image or video must be smaller than 25 MB',
        }),
        'e'
      );
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append('fileupload', file));

    HttpServices.UploadMediaFiles({
      formData,
      setFileUploadprogress: setUploadProgress,
      successCallback: (uploadedFiles) => {
        setMediaFiles((prev) => [...prev, ...(uploadedFiles || [])]);
      },
      failureCallback: (message) => {
        showToast(
          message ||
            translate('agent_inbox_page.failed_to_upload_media', {
              defaultValue: 'Failed to upload media',
            }),
          'e'
        );
      },
    });
    event.target.value = '';
  };

  const removeMediaFile = (mediaURL) => {
    setMediaFiles((prev) => prev.filter((file) => file.media_url !== mediaURL));
  };

  const getMessageMediaURLs = (message) =>
    (message.media_url || '')
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);

  const isVideoURL = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      const mediaPath = parsed.searchParams.get('path') || parsed.pathname;
      return /\.(mp4|webm|mov|m4v)$/i.test(mediaPath);
    } catch (_error) {
      return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || '');
    }
  };

  const getMediaPath = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.searchParams.get('path') || parsed.pathname || '';
    } catch (_error) {
      return url || '';
    }
  };

  const isImageURL = (url) => /\.(jpe?g|png|gif|webp)$/i.test(getMediaPath(url));

  const getAttachmentName = (url) => {
    const mediaPath = getMediaPath(url);
    const fileName = String(mediaPath || '').split('/').pop() || 'Attachment';
    try {
      return decodeURIComponent(fileName);
    } catch (_error) {
      return fileName;
    }
  };

  const openMediaPreview = (url) => {
    if (!url) return;
    if (!isVideoURL(url) && !isImageURL(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setMediaPreview({
      url,
      type: isVideoURL(url) ? 'video' : 'image',
    });
  };

  const renderAttachmentCard = (url, compact = false) => (
    <Paper
      key={url}
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: compact ? 72 : { xs: '100%', sm: 260 },
        height: compact ? 72 : 'auto',
        p: compact ? 1 : 1.25,
        borderRadius: 1.25,
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: compact ? '#f8fafc' : 'rgba(248, 250, 252, 0.95)',
        overflow: 'hidden',
      }}
    >
      <DescriptionOutlinedIcon color="primary" fontSize={compact ? 'medium' : 'small'} />
      {!compact && (
        <Typography variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getAttachmentName(url)}
        </Typography>
      )}
    </Paper>
  );


  const handleTyping = (value) => {
    setNewMessage(value);
    if (
      !selectedConv ||
      selectedConv.status === 'closed'
    )
      return;
    sendLiveChatSocketMessage({
      type: 'agent:typing',
      payload: { conversation_id: selectedConv.id, is_typing: true },
    });
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      sendLiveChatSocketMessage({
        type: 'agent:typing',
        payload: { conversation_id: selectedConv.id, is_typing: false },
      });
    }, 1200);
  };

  const normalizeSearchText = (value) => String(value || '').trim().toLowerCase();
  const truncateWords = (value, limit = 20) => {
    const text = String(value || '').trim();
    if (!text) return '';
    const words = text.split(/\s+/);
    if (words.length <= limit) return text;
    return `${words.slice(0, limit).join(' ')}...`;
  };
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  const getConversationStatus = (conv) => normalizeSearchText(conv?.status || 'open');
  const getConversationUnreadCount = (conv) => Math.max(0, Number(conv?.unread_count || 0));
  const getRelativeBrandTime = (value) => {
    const date = getValidDate(value);
    if (!date) return '';
    const seconds = Math.max(0, Math.floor((CommonHelper.getBrandTimeZoneTime().valueOf() - date.valueOf()) / 1000));
    if (seconds < 45) return translate('agent_inbox_page.just_now', { defaultValue: 'just now' });
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return translate('agent_inbox_page.minutes_ago', {
        defaultValue: '{{count}} mins ago',
        count: minutes,
      });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return translate('agent_inbox_page.hours_ago', {
        defaultValue: '{{count}} hrs ago',
        count: hours,
      });
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return translate('agent_inbox_page.days_ago', {
        defaultValue: '{{count}} days ago',
        count: days,
      });
    }
    return CommonHelper.formatDateAndTime(value);
  };
  const getAbsoluteBrandTime = (value) => {
    const date = getValidDate(value);
    return date ? CommonHelper.formatDateAndTime(value) : '';
  };
  const getConversationActivityTime = (conv) =>
    conv?.last_message_at || conv?.activity_at || conv?.updated_at || conv?.created_at;
  const isMyConversation = (conv) => {
    if (!hasCurrentUserId) return false;
    return [
      conv?.agent_id,
      conv?.assigned_agent_id,
      conv?.assigned_to,
      conv?.user_id,
      conv?.closed_by,
      conv?.last_agent_id,
    ].some((value) => Number(value) === currentUserId);
  };
  const getFilterCount = (filterValue, unreadOnly = false) =>
    conversations.reduce((count, conv) => {
      const status = getConversationStatus(conv);
      const matches =
        filterValue === 'all' ||
        (filterValue === 'mine' ? isMyConversation(conv) : status === filterValue);
      if (!matches) return count;
      return count + (unreadOnly ? getConversationUnreadCount(conv) : 1);
    }, 0);
  const renderFilterLabel = (filterValue, label, icon) => {
    const unreadCount = getFilterCount(filterValue, true);
    return (
      <Tooltip title={label} arrow>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            minWidth: 0,
          }}
        >
          <Badge
            badgeContent={unreadCount > 99 ? '99+' : unreadCount}
            color="error"
            invisible={unreadCount <= 0}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                minWidth: 16,
                height: 16,
                px: 0.4,
                fontSize: '0.62rem',
                top: 1,
                right: 0,
              },
            }}
          >
            {icon}
          </Badge>
          <Typography
            component="span"
            variant="caption"
            sx={{
              fontSize: '0.66rem',
              fontWeight: 600,
              lineHeight: 1,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Tooltip>
    );
  };
  const filteredConversations = conversations.filter((conv) => {
    const conversationStatus = getConversationStatus(conv);
    if (statusFilter === 'mine' && !isMyConversation(conv)) {
      return false;
    }
    if (statusFilter !== 'all' && statusFilter !== 'mine' && conversationStatus !== statusFilter) {
      return false;
    }
    if (!normalizedSearchTerm) return true;
    return [
      conv.id,
      conv.visitor_id,
      conv.visitor_name,
      conv.visitor_email,
      conv.visitor_uid,
      conv.status,
      conv.last_question,
    ]
      .map(normalizeSearchText)
      .some((value) => value.includes(normalizedSearchTerm));
  });
  const selectedClosed = selectedConv?.status === 'closed';
  const mobileFooterOffset = 'calc(72px + env(safe-area-inset-bottom))';
  const mobileInputReserve = selectedClosed
    ? 'calc(124px + 72px + env(safe-area-inset-bottom))'
    : mediaFiles.length > 0
      ? 'calc(246px + 72px + env(safe-area-inset-bottom))'
      : 'calc(132px + 72px + env(safe-area-inset-bottom))';

  return (
    <>
      <Box
        sx={{
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          p: { xs: 1, sm: 1.5, md: 2 },
          pb: { xs: 1.5, md: 2.5 },
          bgcolor: '#f5f7fa',
          overflow: 'hidden',
        }}
      >
        <Grid container spacing={{ xs: 1, md: 2 }} sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {/* Left Sidebar - Conversations */}
        <Grid
          item
          xs={12}
          md={4}
          lg={3}
          sx={{
            height: '100%',
            minHeight: 0,
            display: { xs: selectedConv ? 'none' : 'flex', md: 'flex' },
          }}
        >
          <Paper
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Typography variant="h6">
                {translate('agent_inbox_page.title', { defaultValue: 'Agent Inbox' })}
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                sx={{
                  mt: 1,
                  '& .MuiToggleButton-root': {
                    minWidth: 0,
                    px: 1,
                    py: 0.75,
                    lineHeight: 1,
                  },
                }}
              >
                <ToggleButton value="all">
                  {renderFilterLabel(
                    'all',
                    translate('agent_inbox_page.all_conversations', { defaultValue: 'All' }),
                    <AllInboxIcon fontSize="small" />
                  )}
                </ToggleButton>
                <ToggleButton value="open">
                  {renderFilterLabel(
                    'open',
                    translate('agent_inbox_page.open_conversations', { defaultValue: 'Open' }),
                    <MarkChatUnreadIcon fontSize="small" />
                  )}
                </ToggleButton>
                <ToggleButton value="mine">
                  {renderFilterLabel(
                    'mine',
                    translate('agent_inbox_page.mine_conversations', { defaultValue: 'Mine' }),
                    <PersonPinIcon fontSize="small" />
                  )}
                </ToggleButton>
                <ToggleButton value="closed">
                  {renderFilterLabel(
                    'closed',
                    translate('agent_inbox_page.closed_conversations', { defaultValue: 'Closed' }),
                    <Inventory2OutlinedIcon fontSize="small" />
                  )}
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                fullWidth
                size="small"
                placeholder={translate('agent_inbox_page.search_placeholder', {
                  defaultValue: 'Search name, email, ID...',
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mt: 1 }}
              />
            </Box>

            <List
              onScroll={handleConversationsScroll}
              sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
            >
              {filteredConversations.length === 0 ? (
                <Typography align="center" sx={{ mt: 5, color: 'text.secondary' }}>
                  {translate('agent_inbox_page.no_conversations', {
                    defaultValue: 'No conversations found.',
                  })}
                </Typography>
              ) : (
                filteredConversations.map((conv) => {
                  const activityTime = getConversationActivityTime(conv);
                  const relativeTime = getRelativeBrandTime(activityTime);
                  const absoluteTime = getAbsoluteBrandTime(activityTime);
                  return (
                    <ListItem
                      button
                      key={conv.id}
                      selected={selectedConv?.id === conv.id}
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <ListItemAvatar>
                        <VisitorFlagAvatar conversation={conv} fallback={String.fromCodePoint(0x1f30d)} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {conv.visitor_name ||
                                translate('agent_inbox_page.anonymous', { defaultValue: 'Anonymous' })}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                conv.status === 'closed'
                                  ? translate('agent_inbox_page.closed_status', { defaultValue: 'Closed' })
                                  : translate('agent_inbox_page.open_status', { defaultValue: 'Open' })
                              }
                              color={
                                conv.status === 'closed'
                                  ? 'error'
                                  : 'success'
                              }
                              variant="filled"
                            />
                          </Stack>
                        }
                        secondary={
                          <>
                            <Stack direction="row" spacing={1} alignItems="center" component="span">
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {conv.visitor_email ||
                                  conv.visitor_uid ||
                                  translate('agent_inbox_page.no_visitor_email', {
                                    defaultValue: 'No visitor email',
                                  })}
                              </Typography>
                              {relativeTime && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  title={absoluteTime}
                                  sx={{ flexShrink: 0 }}
                                >
                                  {relativeTime}
                                </Typography>
                              )}
                            </Stack>
                            {conv.last_question && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.primary"
                                sx={{
                                  display: 'block',
                                  mt: 0.25,
                                  maxWidth: 230,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {translate('agent_inbox_page.last_question', {
                                  defaultValue: 'Last question: {{question}}',
                                  question: truncateWords(conv.last_question, 20),
                                })}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      {conv.unread_count > 0 && <Badge badgeContent={conv.unread_count} color="error" />}
                    </ListItem>
                  );
                })
              )}
              {conversationsLoader && (
                <Box sx={{ px: 2, py: 1 }}>
                  <LinearProgress />
                </Box>
              )}
              {!conversationsLoader && hasMoreConversations && filteredConversations.length > 0 && (
                <Box sx={{ height: 8 }} />
              )}
            </List>
          </Paper>
        </Grid>

        {/* Chat Area */}
        <Grid
          item
          xs={12}
          md={8}
          lg={9}
          sx={{
            height: '100%',
            minHeight: 0,
            display: { xs: selectedConv ? 'flex' : 'none', md: 'flex' },
          }}
        >
          <Paper
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {selectedConv ? (
              <>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc', flexShrink: 0 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedConv(null);
                        setMessages([]);
                        setMediaFiles([]);
                      }}
                      sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                    >
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <VisitorFlagAvatar conversation={selectedConv} fallback={String.fromCodePoint(0x1f464)} />
                    <Box flex={1}>
                      <Typography variant="h6">
                        {selectedConv.visitor_name ||
                          translate('agent_inbox_page.visitor', { defaultValue: 'Visitor' })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedConv.visitor_email}
                      </Typography>
                      {selectedConv.last_question && (
                        <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                          {translate('agent_inbox_page.last_question', {
                            defaultValue: 'Last question: {{question}}',
                            question: truncateWords(selectedConv.last_question, 20),
                          })}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={
                        selectedClosed
                          ? translate('agent_inbox_page.closed_status', { defaultValue: 'Closed' })
                          : translate('agent_inbox_page.open_status', { defaultValue: 'Open' })
                      }
                      color={
                        selectedClosed
                          ? 'error'
                          : 'success'
                      }
                      size="small"
                      variant="filled"
                    />
                    <Chip
                      label={translate('agent_inbox_page.agents_online', {
                        defaultValue: 'Online agents: {{count}}',
                        count: Object.values(onlineAgents).filter(Boolean).length,
                      })}
                      size="small"
                      variant="filled"
                      sx={{
                        height: 28,
                        px: 0.5,
                        border: '1px solid',
                        borderColor: 'success.light',
                        bgcolor: 'rgba(34, 197, 94, 0.12)',
                        color: 'success.dark',
                        fontWeight: 800,
                        '& .MuiChip-label': {
                          px: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          '&::before': {
                            content: '""',
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.16)',
                          },
                        },
                      }}
                    />
                    {!selectedClosed ? (
                      <>
                        {ticketProviderConfigured && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={createTicketLoader}
                            onClick={() => setCreateTicketConfirmOpen(true)}
                          >
                            {createTicketLoader
                              ? translate('agent_inbox_page.creating_ticket', {
                                  defaultValue: 'Creating ticket...',
                                })
                              : translate('agent_inbox_page.create_ticket', {
                                  defaultValue: 'Create ticket',
                                })}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          disabled={closeLoader || createTicketLoader}
                          onClick={() => setStatusConfirmAction('close')}
                        >
                          {translate('agent_inbox_page.close_conversation', {
                            defaultValue: 'Close conversation',
                          })}
                        </Button>
                      </>
                    ) : null}
                  </Stack>
                </Box>

                {/* Messages */}
                <Box
                  ref={messagesContainerRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    p: { xs: 1.5, md: 3 },
                    pb: { xs: mobileInputReserve, md: 3 },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    bgcolor: '#f8fafc',
                  }}
                >
                  {messages.map((msg, i) => {
                    const isAgentMessage =
                      msg.sender_type === 'agent' ||
                      (hasCurrentUserId &&
                        (Number(msg.sender_id) === currentUserId || Number(msg.senderId) === currentUserId));
                    const messageRelativeTime = getRelativeBrandTime(msg.created_at);
                    const messageAbsoluteTime = getAbsoluteBrandTime(msg.created_at);
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          justifyContent: isAgentMessage ? 'flex-end' : 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: isAgentMessage ? '70%' : { xs: '92%', md: 760 },
                            p: isAgentMessage ? 2 : 2.25,
                            borderRadius: isAgentMessage ? 3 : 2.5,
                            bgcolor: isAgentMessage ? '#2563eb' : '#fff',
                            color: isAgentMessage ? '#fff' : '#000',
                            boxShadow: isAgentMessage ? 1 : '0 10px 28px rgba(15, 23, 42, 0.06)',
                            border: isAgentMessage ? 'none' : '1px solid #e2e8f0',
                          }}
                        >
                          {isAgentMessage && (
                            <Typography
                              variant="caption"
                              sx={{ opacity: 0.85, display: 'block', mb: 0.5, fontWeight: 700 }}
                            >
                              {msg.sender_name ||
                                translate('agent_inbox_page.agent', { defaultValue: 'Agent' })}
                            </Typography>
                          )}
                          <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>
                            {msg.message}
                          </Typography>
                          {getMessageMediaURLs(msg).map((url) => (
                            <Box key={url} sx={{ mt: msg.message ? 1 : 0 }}>
                              {isVideoURL(url) ? (
                                <video
                                  src={url}
                                  controls
                                  onClick={() => openMediaPreview(url)}
                                  style={{ maxWidth: 260, width: '100%', borderRadius: 10, cursor: 'pointer' }}
                                />
                              ) : isImageURL(url) ? (
                                <Box
                                  component="img"
                                  src={url}
                                  alt="Chat media"
                                  onClick={() => openMediaPreview(url)}
                                  sx={{ maxWidth: 260, width: '100%', borderRadius: 1.25, display: 'block', cursor: 'pointer' }}
                                />
                              ) : (
                                renderAttachmentCard(url)
                              )}
                            </Box>
                          ))}
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, mt: 0.5, display: 'block', textAlign: 'right' }}
                            title={messageAbsoluteTime}
                          >
                            {messageRelativeTime || messageAbsoluteTime}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Input */}
                <Box
                  sx={{
                    position: { xs: 'fixed', md: 'static' },
                    left: { xs: 0, md: 'auto' },
                    right: { xs: 0, md: 'auto' },
                    bottom: { xs: mobileFooterOffset, md: 'auto' },
                    zIndex: { xs: 1199, md: 'auto' },
                    p: { xs: 1, md: 2 },
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: '#fff',
                    flexShrink: 0,
                    mb: { md: 1 },
                    boxShadow: { xs: '0 -10px 24px rgba(15, 23, 42, 0.08)', md: 'none' },
                  }}
                >
                  {selectedClosed ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        bgcolor: '#f8fafc',
                        borderStyle: 'dashed',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="subtitle2">
                        {translate('agent_inbox_page.conversation_closed', {
                          defaultValue: 'This conversation is closed',
                        })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translate('agent_inbox_page.closed_note', {
                          defaultValue:
                            'Previous messages are available above. Replies and uploads are disabled for closed chats.',
                        })}
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      {typingState.visitor && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {translate('agent_inbox_page.typing', {
                            defaultValue: 'Visitor is typing...',
                          })}
                        </Typography>
                      )}
                      {typingState.agent && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {translate('agent_inbox_page.agent_typing', {
                            defaultValue: 'Another agent is typing...',
                          })}
                        </Typography>
                      )}
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 1 }} />
                      )}
                      {mediaFiles.length > 0 && (
                        <Stack direction="row" spacing={1} sx={{ mb: 1, overflowX: 'auto' }}>
                          {mediaFiles.map((file) => (
                            <Box
                              key={file.media_url}
                              sx={{ position: 'relative', width: 72, height: 72, flex: '0 0 auto' }}
                            >
                              {file.media_type?.toUpperCase() === 'VIDEO' || isVideoURL(file.media_url) ? (
                                <video
                                  src={file.media_url}
                                  onClick={() => openMediaPreview(file.media_url)}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                                />
                              ) : isImageURL(file.media_url) ? (
                                <Box
                                  component="img"
                                  src={file.media_url}
                                  alt="Upload preview"
                                  onClick={() => openMediaPreview(file.media_url)}
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                                />
                              ) : (
                                renderAttachmentCard(file.media_url, true)
                              )}
                              <IconButton
                                size="small"
                                onClick={() => removeMediaFile(file.media_url)}
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  bgcolor: 'background.paper',
                                  boxShadow: 1,
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      )}
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          hidden
                          accept="image/*,video/*"
                          onChange={handleMediaUpload}
                        />
                        <IconButton
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadProgress > 0 && uploadProgress < 100}
                          sx={{ flexShrink: 0 }}
                        >
                          <AttachFileIcon />
                        </IconButton>
                        <TextField
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={4}
                          placeholder={translate('agent_inbox_page.reply_placeholder', {
                            defaultValue: 'Type your reply...',
                          })}
                          value={newMessage}
                          sx={{ minWidth: 0 }}
                          onChange={(e) => handleTyping(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                        />
                        <IconButton
                          color="primary"
                          onClick={sendMessage}
                          disabled={!newMessage.trim() && mediaFiles.length === 0}
                          sx={{ flexShrink: 0 }}
                        >
                          <SendIcon />
                        </IconButton>
                      </Stack>
                    </>
                  )}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h5" color="text.secondary">
                  {translate('agent_inbox_page.title', { defaultValue: 'Agent Inbox' })}
                </Typography>
                <Typography color="text.secondary">
                  {translate('agent_inbox_page.no_conversation_selected', {
                    defaultValue: 'Select a conversation to view messages.',
                  })}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        </Grid>
      </Box>
      <Dialog
        open={Boolean(mediaPreview)}
        onClose={() => setMediaPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
      >
        <DialogContent
          sx={{
            p: 0,
            bgcolor: 'rgba(15, 23, 42, 0.92)',
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => setMediaPreview(null)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1,
              bgcolor: 'rgba(255,255,255,0.12)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {mediaPreview?.type === 'video' ? (
            <Box
              component="video"
              src={mediaPreview.url}
              controls
              autoPlay
              sx={{ display: 'block', width: '100%', maxHeight: '85vh', objectFit: 'contain', bgcolor: '#000' }}
            />
          ) : (
            <Box
              component="img"
              src={mediaPreview?.url}
              alt="Chat media preview"
              sx={{ display: 'block', width: '100%', maxHeight: '85vh', objectFit: 'contain', bgcolor: '#000' }}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={Boolean(statusConfirmAction)}
        onClose={() => !closeLoader && setStatusConfirmAction(null)}
        onConfirm={confirmConversationStatusChange}
        loading={closeLoader}
        title={translate('agent_inbox_page.close_conversation', { defaultValue: 'Close conversation' })}
        message={translate('agent_inbox_page.close_conversation_confirm', {
          defaultValue: 'Are you sure you want to close this conversation?',
        })}
        submitButtonName={translate('agent_inbox_page.close', { defaultValue: 'Close' })}
      />
      <ConfirmationDialog
        open={createTicketConfirmOpen}
        onClose={() => !createTicketLoader && setCreateTicketConfirmOpen(false)}
        onConfirm={createTicketFromConversation}
        loading={createTicketLoader}
        title={translate('agent_inbox_page.create_ticket', {
          defaultValue: 'Create ticket',
        })}
        message={translate('agent_inbox_page.create_ticket_confirm', {
          defaultValue: 'Are you sure you want to create a ticket from this conversation?',
        })}
        submitButtonName={translate('agent_inbox_page.create_ticket', {
          defaultValue: 'Create ticket',
        })}
      />
    </>
  );
};

export default AgentInbox;
